import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, formatDate } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Plus, Search, Archive, Edit3, X, Save, PackageOpen, ArrowLeft } from "lucide-react";

const SUPPORTED_TRADES = [
  "Roofing", "Flooring", "Painting", "Drywall",
  "Concrete", "Restoration", "Plumbing", "HVAC",
];

const emptyForm = () => ({
  trade: "",
  system_type: "",
  category: "",
  supplier: "",
  brand: "",
  product: "",
  sku: "",
  unit: "each",
  coverage_per_unit: "",
  contractor_cost: "",
  waste_pct: "",
  markup_pct: "",
  active: true,
  notes: "",
});

const MaterialPriceBook = () => {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState({
    trade: "",
    system_type: "",
    supplier: "",
    brand: "",
    active: "true", // "true" | "false" | ""
    q: "",
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  // ----- Fetch -----
  // Keep a ref mirror of the current filters so ``fetchItems`` always reads
  // the latest ``q`` (which is deliberately NOT in ``filterKey`` — we don't
  // want a fetch on every keystroke). The memoization key ``filterKey`` only
  // controls when useEffect re-fires on dropdown changes; the actual filter
  // read happens through ``filtersRef.current`` at call time.
  const filterKey = `${filters.trade}|${filters.system_type}|${filters.supplier}|${filters.brand}|${filters.active}`;
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const current = filtersRef.current;
      const params = {};
      for (const k of ["trade", "system_type", "supplier", "brand", "q"]) {
        if (current[k]) params[k] = current[k];
      }
      if (current.active !== "") params.active = current.active;
      const { data } = await api.get("/material-price-book", { params });
      setItems(data || []);
    } catch {
      toast.error(t("material_price_book.toast_save_failed"));
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-fetch when the dropdown filters (trade/system_type/supplier/brand/active)
  // change. ``q`` is intentionally excluded — Search button click / Enter is the
  // trigger for free-text search.
  useEffect(() => {
    fetchItems();
  }, [filterKey, fetchItems]);

  const onSearch = (e) => {
    e.preventDefault();
    fetchItems();
  };

  // Derive supplier + brand suggestions from the current list for the filter dropdowns.
  const suppliers = useMemo(() => Array.from(new Set(items.map((i) => i.supplier).filter(Boolean))).sort(), [items]);
  const brands = useMemo(() => Array.from(new Set(items.map((i) => i.brand).filter(Boolean))).sort(), [items]);

  // ----- Modal open / edit / save -----
  const openCreate = () => {
    setForm(emptyForm());
    setEditingId(null);
    setModalOpen(true);
  };
  const openEdit = (item) => {
    setForm({
      trade: item.trade || "",
      system_type: item.system_type || "",
      category: item.category || "",
      supplier: item.supplier || "",
      brand: item.brand || "",
      product: item.product || "",
      sku: item.sku || "",
      unit: item.unit || "each",
      coverage_per_unit: item.coverage_per_unit ?? "",
      contractor_cost: item.contractor_cost ?? "",
      waste_pct: item.waste_pct ?? "",
      markup_pct: item.markup_pct ?? "",
      active: item.active !== false,
      notes: item.notes || "",
    });
    setEditingId(item.id);
    setModalOpen(true);
  };

  const numOrNull = (v) => {
    if (v === "" || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const save = async () => {
    if (!form.trade || !form.product) {
      toast.error(t("material_price_book.validation_trade_product_required"));
      return;
    }
    setBusy(true);
    try {
      const payload = {
        trade: form.trade,
        system_type: form.system_type || "",
        category: form.category || "",
        supplier: form.supplier || "",
        brand: form.brand || "",
        product: form.product,
        sku: form.sku || "",
        unit: form.unit || "each",
        coverage_per_unit: numOrNull(form.coverage_per_unit),
        contractor_cost: Number(form.contractor_cost) || 0,
        waste_pct: numOrNull(form.waste_pct) ?? 0,
        markup_pct: numOrNull(form.markup_pct),
        active: !!form.active,
        notes: form.notes || "",
      };
      if (editingId) {
        await api.put(`/material-price-book/${editingId}`, payload);
        toast.success(t("material_price_book.toast_updated"));
      } else {
        await api.post("/material-price-book", payload);
        toast.success(t("material_price_book.toast_added"));
      }
      setModalOpen(false);
      setEditingId(null);
      fetchItems();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("material_price_book.toast_save_failed"));
    } finally {
      setBusy(false);
    }
  };

  const archive = async (item) => {
    if (!window.confirm(t("material_price_book.confirm_archive_prompt", item.product))) return;
    setBusy(true);
    try {
      await api.delete(`/material-price-book/${item.id}`);
      toast.success(t("material_price_book.toast_archived"));
      fetchItems();
    } catch {
      toast.error(t("material_price_book.toast_archive_failed"));
    } finally {
      setBusy(false);
    }
  };

  const reactivate = async (item) => {
    setBusy(true);
    try {
      // Reuse PUT with same payload but active=true.
      const payload = {
        trade: item.trade,
        system_type: item.system_type || "",
        category: item.category || "",
        supplier: item.supplier || "",
        brand: item.brand || "",
        product: item.product,
        sku: item.sku || "",
        unit: item.unit || "each",
        coverage_per_unit: item.coverage_per_unit ?? null,
        contractor_cost: item.contractor_cost || 0,
        waste_pct: item.waste_pct ?? 0,
        markup_pct: item.markup_pct ?? null,
        active: true,
        notes: item.notes || "",
      };
      await api.put(`/material-price-book/${item.id}`, payload);
      toast.success(t("material_price_book.toast_reactivated"));
      fetchItems();
    } catch {
      toast.error(t("material_price_book.toast_reactivate_failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DashboardLayout
      title={t("material_price_book.title")}
      subtitle={t("material_price_book.subtitle")}
      actions={
        <div className="flex items-center gap-2">
          <Link
            to="/app/pricing-profile"
            data-testid="materials-back-link"
            className="text-sm font-semibold text-[var(--jc-text-soft)] hover:text-[var(--jc-text)] inline-flex items-center gap-1"
          >
            <ArrowLeft size={14} /> {t("nav.pricing_settings") || "Pricing profile"}
          </Link>
          <button
            onClick={openCreate}
            data-testid="materials-add-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md jc-btn-primary"
          >
            <Plus size={14} /> {t("material_price_book.add_item")}
          </button>
        </div>
      }
    >
      <div data-testid="materials-page" className="space-y-4">
        {/* Contractor-internal reminder banner */}
        <div className="rounded-md border border-[var(--jc-border)] bg-[var(--jc-bg)] px-4 py-3 text-xs text-[var(--jc-text-soft)]">
          <b className="text-[var(--jc-text)]">{t("material_price_book.contractor_internal_prefix")}</b>{" "}
          {t("material_price_book.contractor_internal_body")}{" "}
          {t("material_price_book.banner_contractor_only_body")}
        </div>

        {/* Filters */}
        <form
          onSubmit={onSearch}
          className="jc-card p-4 grid gap-3 lg:grid-cols-6"
          data-testid="materials-filters"
        >
          <div>
            <label className="block text-[11px] font-semibold text-[var(--jc-text-soft)] uppercase mb-1">{t("material_price_book.filter_trade")}</label>
            <select
              value={filters.trade}
              onChange={(e) => setFilters((f) => ({ ...f, trade: e.target.value, system_type: "" }))}
              data-testid="materials-filter-trade"
              className="w-full px-2 py-1.5 rounded border border-[var(--jc-border-strong)] text-sm"
            >
              <option value="">{t("material_price_book.filter_all_trades")}</option>
              {SUPPORTED_TRADES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-[var(--jc-text-soft)] uppercase mb-1">{t("material_price_book.filter_system")}</label>
            <input
              value={filters.system_type}
              onChange={(e) => setFilters((f) => ({ ...f, system_type: e.target.value }))}
              placeholder={t("material_price_book.filter_all_systems")}
              data-testid="materials-filter-system-type"
              className="w-full px-2 py-1.5 rounded border border-[var(--jc-border-strong)] text-sm"
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-[var(--jc-text-soft)] uppercase mb-1">{t("material_price_book.filter_supplier")}</label>
            <select
              value={filters.supplier}
              onChange={(e) => setFilters((f) => ({ ...f, supplier: e.target.value }))}
              data-testid="materials-filter-supplier"
              className="w-full px-2 py-1.5 rounded border border-[var(--jc-border-strong)] text-sm"
            >
              <option value="">{t("material_price_book.filter_all_suppliers")}</option>
              {suppliers.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-[var(--jc-text-soft)] uppercase mb-1">{t("material_price_book.filter_brand")}</label>
            <select
              value={filters.brand}
              onChange={(e) => setFilters((f) => ({ ...f, brand: e.target.value }))}
              data-testid="materials-filter-brand"
              className="w-full px-2 py-1.5 rounded border border-[var(--jc-border-strong)] text-sm"
            >
              <option value="">{t("material_price_book.filter_all_brands")}</option>
              {brands.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-[var(--jc-text-soft)] uppercase mb-1">{t("material_price_book.filter_status")}</label>
            <select
              value={filters.active}
              onChange={(e) => setFilters((f) => ({ ...f, active: e.target.value }))}
              data-testid="materials-filter-active"
              className="w-full px-2 py-1.5 rounded border border-[var(--jc-border-strong)] text-sm"
            >
              <option value="true">{t("material_price_book.filter_active_only")}</option>
              <option value="false">{t("material_price_book.filter_archived_only")}</option>
              <option value="">{t("material_price_book.filter_all_status")}</option>
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-[var(--jc-text-soft)] uppercase mb-1">{t("material_price_book.filter_search")}</label>
            <div className="flex gap-1">
              <input
                value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                placeholder={t("material_price_book.search_hint")}
                data-testid="materials-filter-q"
                className="flex-1 px-2 py-1.5 rounded border border-[var(--jc-border-strong)] text-sm"
              />
              <button
                type="submit"
                data-testid="materials-filter-search-btn"
                className="px-2 py-1.5 rounded border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
              >
                <Search size={14} />
              </button>
            </div>
          </div>
        </form>

        {/* Table */}
        <div className="jc-card overflow-hidden">
          {loading ? (
            <div className="p-6 text-sm text-[var(--jc-text-soft)]">{t("material_price_book.loading")}</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center" data-testid="materials-empty-state">
              <PackageOpen className="mx-auto text-[var(--jc-text-soft)]" size={32} />
              <div className="mt-3 text-sm font-semibold">{t("material_price_book.empty_body_short")}</div>
              <div className="mt-1 text-xs text-[var(--jc-text-soft)]">
                {t("material_price_book.empty_hint")}
              </div>
              <button
                onClick={openCreate}
                data-testid="materials-empty-add-btn"
                className="mt-4 inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md jc-btn-primary"
              >
                <Plus size={14} /> {t("material_price_book.add_item")}
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="materials-table">
                <thead className="bg-[var(--jc-bg)] text-left text-[11px] uppercase tracking-wider text-[var(--jc-text-soft)]">
                  <tr>
                    <th className="px-3 py-2">{t("material_price_book.col_product")}</th>
                    <th className="px-3 py-2">{t("material_price_book.col_trade_system")}</th>
                    <th className="px-3 py-2">{t("material_price_book.col_supplier_brand")}</th>
                    <th className="px-3 py-2">SKU</th>
                    <th className="px-3 py-2 text-right">{t("material_price_book.col_cost")}</th>
                    <th className="px-3 py-2 text-right">{t("material_price_book.col_waste_pct")}</th>
                    <th className="px-3 py-2">{t("material_price_book.col_unit")}</th>
                    <th className="px-3 py-2">{t("material_price_book.col_status")}</th>
                    <th className="px-3 py-2 text-right">{t("material_price_book.col_updated")}</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr
                      key={item.id}
                      data-testid={`materials-row-${item.id}`}
                      className="border-t border-[var(--jc-border)] hover:bg-[var(--jc-bg)]/40"
                    >
                      <td className="px-3 py-2">
                        <div className="font-semibold">{item.product}</div>
                        {item.category && <div className="text-[11px] text-[var(--jc-text-soft)]">{item.category}</div>}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <div>{item.trade}</div>
                        {item.system_type && <div className="text-[var(--jc-text-soft)]">{item.system_type}</div>}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <div>{item.supplier || "—"}</div>
                        <div className="text-[var(--jc-text-soft)]">{item.brand || "—"}</div>
                      </td>
                      <td className="px-3 py-2 text-xs font-mono-jc">{item.sku || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono-jc">${Number(item.contractor_cost || 0).toFixed(2)}</td>
                      <td className="px-3 py-2 text-right font-mono-jc">{Number(item.waste_pct || 0).toFixed(1)}%</td>
                      <td className="px-3 py-2 text-xs">{item.unit}</td>
                      <td className="px-3 py-2">
                        {item.active === false ? (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold bg-neutral-200 text-neutral-700">{t("material_price_book.row_archived")}</span>
                        ) : (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700">{t("material_price_book.row_active")}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-[var(--jc-text-soft)] text-right whitespace-nowrap">
                        {item.updated_at ? formatDate(item.updated_at) : "—"}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button
                          onClick={() => openEdit(item)}
                          data-testid={`materials-edit-btn-${item.id}`}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)] mr-1"
                        >
                          <Edit3 size={12} /> {t("material_price_book.row_edit")}
                        </button>
                        {item.active === false ? (
                          <button
                            onClick={() => reactivate(item)}
                            data-testid={`materials-reactivate-btn-${item.id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          >
                            {t("material_price_book.row_restore")}
                          </button>
                        ) : (
                          <button
                            onClick={() => archive(item)}
                            data-testid={`materials-archive-btn-${item.id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
                          >
                            <Archive size={12} /> {t("material_price_book.row_archive")}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          data-testid="materials-modal"
        >
          <div className="jc-card w-full max-w-2xl max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--jc-border)]">
              <h2 className="font-display text-xl tracking-tight">
                {editingId ? t("material_price_book.modal_edit_title") : t("material_price_book.modal_add_title")}
              </h2>
              <button
                onClick={() => setModalOpen(false)}
                data-testid="materials-modal-close-btn"
                className="p-1 hover:bg-[var(--jc-bg)] rounded"
              >
                <X size={16} />
              </button>
            </div>
            <div className="p-5 grid gap-3 sm:grid-cols-2">
              <FormField label={t("material_price_book.field_trade_required")} testId="materials-form-trade">
                <select
                  value={form.trade}
                  onChange={(e) => setForm((f) => ({ ...f, trade: e.target.value }))}
                  data-testid="materials-form-trade-input"
                  className="w-full px-2 py-1.5 rounded border border-[var(--jc-border-strong)] text-sm"
                >
                  <option value="">{t("material_price_book.field_trade_select")}</option>
                  {SUPPORTED_TRADES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </FormField>
              <FormField label={t("material_price_book.field_system_type")} testId="materials-form-system-type">
                <input
                  value={form.system_type}
                  onChange={(e) => setForm((f) => ({ ...f, system_type: e.target.value }))}
                  data-testid="materials-form-system-type-input"
                  placeholder={t("material_price_book.placeholder_system_type")}
                  className="w-full px-2 py-1.5 rounded border border-[var(--jc-border-strong)] text-sm"
                />
              </FormField>
              <FormField label={t("material_price_book.field_category")} testId="materials-form-category">
                <input
                  value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  data-testid="materials-form-category-input"
                  placeholder={t("material_price_book.placeholder_category")}
                  className="w-full px-2 py-1.5 rounded border border-[var(--jc-border-strong)] text-sm"
                />
              </FormField>
              <FormField label={t("material_price_book.field_supplier_vendor")} testId="materials-form-supplier">
                <input
                  value={form.supplier}
                  onChange={(e) => setForm((f) => ({ ...f, supplier: e.target.value }))}
                  data-testid="materials-form-supplier-input"
                  placeholder={t("material_price_book.placeholder_supplier")}
                  className="w-full px-2 py-1.5 rounded border border-[var(--jc-border-strong)] text-sm"
                />
              </FormField>
              <FormField label={t("material_price_book.field_brand_manufacturer")} testId="materials-form-brand">
                <input
                  value={form.brand}
                  onChange={(e) => setForm((f) => ({ ...f, brand: e.target.value }))}
                  data-testid="materials-form-brand-input"
                  placeholder={t("material_price_book.placeholder_brand")}
                  className="w-full px-2 py-1.5 rounded border border-[var(--jc-border-strong)] text-sm"
                />
              </FormField>
              <FormField label={t("material_price_book.field_product_required")} testId="materials-form-product">
                <input
                  value={form.product}
                  onChange={(e) => setForm((f) => ({ ...f, product: e.target.value }))}
                  data-testid="materials-form-product-input"
                  placeholder={t("material_price_book.placeholder_product")}
                  className="w-full px-2 py-1.5 rounded border border-[var(--jc-border-strong)] text-sm"
                />
              </FormField>
              <FormField label={t("material_price_book.field_sku_optional")} testId="materials-form-sku">
                <input
                  value={form.sku}
                  onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))}
                  data-testid="materials-form-sku-input"
                  placeholder={t("material_price_book.field_sku_placeholder")}
                  className="w-full px-2 py-1.5 rounded border border-[var(--jc-border-strong)] text-sm font-mono-jc"
                />
              </FormField>
              <FormField label={t("material_price_book.field_unit_of_measure")} testId="materials-form-unit">
                <input
                  value={form.unit}
                  onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
                  data-testid="materials-form-unit-input"
                  placeholder={t("material_price_book.placeholder_unit")}
                  className="w-full px-2 py-1.5 rounded border border-[var(--jc-border-strong)] text-sm"
                />
              </FormField>
              <FormField label={t("material_price_book.field_coverage_per_unit")} testId="materials-form-coverage">
                <input
                  type="number"
                  step="any"
                  value={form.coverage_per_unit}
                  onChange={(e) => setForm((f) => ({ ...f, coverage_per_unit: e.target.value }))}
                  data-testid="materials-form-coverage-input"
                  placeholder={t("material_price_book.placeholder_coverage")}
                  className="w-full px-2 py-1.5 rounded border border-[var(--jc-border-strong)] text-sm font-mono-jc text-right"
                />
              </FormField>
              <FormField label={t("material_price_book.field_contractor_cost_required")} testId="materials-form-cost">
                <div className="flex items-center gap-1">
                  <span className="text-sm text-[var(--jc-text-soft)]">$</span>
                  <input
                    type="number"
                    step="any"
                    value={form.contractor_cost}
                    onChange={(e) => setForm((f) => ({ ...f, contractor_cost: e.target.value }))}
                    data-testid="materials-form-cost-input"
                    placeholder="0.00"
                    className="flex-1 px-2 py-1.5 rounded border border-[var(--jc-border-strong)] text-sm font-mono-jc text-right"
                  />
                </div>
              </FormField>
              <FormField label={t("material_price_book.field_default_waste_pct")} testId="materials-form-waste">
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    step="any"
                    value={form.waste_pct}
                    onChange={(e) => setForm((f) => ({ ...f, waste_pct: e.target.value }))}
                    data-testid="materials-form-waste-input"
                    placeholder="0"
                    className="flex-1 px-2 py-1.5 rounded border border-[var(--jc-border-strong)] text-sm font-mono-jc text-right"
                  />
                  <span className="text-sm text-[var(--jc-text-soft)]">%</span>
                </div>
              </FormField>
              <FormField label={t("material_price_book.field_markup_optional")} testId="materials-form-markup">
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    step="any"
                    value={form.markup_pct}
                    onChange={(e) => setForm((f) => ({ ...f, markup_pct: e.target.value }))}
                    data-testid="materials-form-markup-input"
                    placeholder={t("material_price_book.placeholder_markup")}
                    className="flex-1 px-2 py-1.5 rounded border border-[var(--jc-border-strong)] text-sm font-mono-jc text-right"
                  />
                  <span className="text-sm text-[var(--jc-text-soft)]">%</span>
                </div>
              </FormField>
              <FormField label={t("material_price_book.field_active")} testId="materials-form-active">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                    data-testid="materials-form-active-input"
                  />
                  {t("material_price_book.active_in_use")}
                </label>
              </FormField>
              <div className="sm:col-span-2">
                <FormField label={t("material_price_book.field_notes")} testId="materials-form-notes">
                  <textarea
                    rows={2}
                    value={form.notes}
                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                    data-testid="materials-form-notes-input"
                    placeholder={t("material_price_book.field_notes_placeholder")}
                    className="w-full px-2 py-1.5 rounded border border-[var(--jc-border-strong)] text-sm"
                  />
                </FormField>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[var(--jc-border)] bg-[var(--jc-bg)]/40">
              <button
                onClick={() => setModalOpen(false)}
                data-testid="materials-modal-cancel-btn"
                className="px-3 py-1.5 text-sm font-semibold rounded border border-[var(--jc-border-strong)] hover:bg-white"
              >
                {t("material_price_book.cancel")}
              </button>
              <button
                onClick={save}
                disabled={busy}
                data-testid="materials-modal-save-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded jc-btn-primary disabled:opacity-60"
              >
                <Save size={14} /> {editingId ? t("material_price_book.modal_save_changes") : t("material_price_book.modal_add_title")}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

const FormField = ({ label, testId, children }) => (
  <div data-testid={testId}>
    <label className="block text-[11px] font-semibold text-[var(--jc-text-soft)] uppercase mb-1">{label}</label>
    {children}
  </div>
);

export default MaterialPriceBook;
